import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {Disposers} from '../../../../../../lib/helpers';
import {X9ContentStateStore} from '../../../common/X9ContentStateStore';
import {SyncedVideoModel} from '../../models';
import {VideoElementStore} from '../common/VideoElementStore';
import {VideoProgressStore} from '../common/VideoProgressStore';
import {PlayTimeRange, StudentPlayerState, SyncedVideoState} from '../models';

export class TeacherSyncedVideoStore {
  readonly video = new VideoElementStore();
  readonly progress = new VideoProgressStore();

  private readonly disposers = new Disposers();

  @observable.shallow
  readonly trackedStudents = new Map<number, VideoProgressStore>();

  constructor(readonly id: number,
              readonly model: SyncedVideoModel,
              readonly stateStore: X9ContentStateStore) {
    makeObservable(this);
    this.disposers.add(this.video);
    this.disposers.add(this.progress);
    this.setup();
  }

  private setup() {
    const command = this.stateStore.getScopedContentState<PlayTimeRange>(this.id, 'command');
    if (command !== null && typeof command.commandId === 'number')
      this.video.playCount = command.commandId;


    this.progress.syncVideoDurationFrom(this.video);
    this.progress.syncCurrentTimeFrom(this.video);
    this.progress.syncBufferedFrom(this.video);

    this.disposers.add(reaction(
      () => [this.video.playCount, this.video.playFrom, this.video.paused, this.video.ended],
      this.sendCommands,
      {fireImmediately: true}
    ));
    this.disposers.add(reaction(
      () => this.stateStore.getContentState<SyncedVideoState>(this.id),
      this.trackStudents,
      {fireImmediately: true}
    ));
  }

  @action
  private sendCommands = () => {
    let command: PlayTimeRange;
    const video = this.video;
    if (video.paused || video.ended) {
      if (video.currentTime === video.playFrom)
        command = {commandId: video.playCount, start: null, stop: null};
      else
        command = {commandId: video.playCount, start: video.playFrom, stop: video.currentTime};
    }
    else
      command = {commandId: video.playCount, start: video.playFrom, stop: null};
    this.stateStore.setScopedContentState<PlayTimeRange>(this.id, 'command', command);
  };

  @computed
  private get syncedVideoStudents(): SyncedVideoState {
    const studentIds: number[] = [];
    const altSyncedVideoStudents: SyncedVideoState = { studentIds };
    return this.stateStore.getContentState<SyncedVideoState>(this.id) ? this.stateStore.getContentState<SyncedVideoState>(this.id)! : altSyncedVideoStudents;
  }

  @action
  private trackStudents = () => {
    const students = this.syncedVideoStudents;

    for (let studentId of students.studentIds) {
      if (this.trackedStudents.has(studentId))
        continue;
      const progress = new VideoProgressStore();
      progress.syncVideoDurationFrom(this.video);
      const studentStateProvider =
        () => this.stateStore.getScopedContentState<StudentPlayerState>(this.id, studentId);
      progress.syncStudentState(studentStateProvider);
      this.trackedStudents.set(studentId, progress);
      this.disposers.add(progress);
    }
  }

  dispose() {
    this.disposers.dispose();
  }
}
